/**
 * Workout Store
 *
 * Zustand store for managing workout template state, CRUD operations, and exercise favorites.
 * Provides centralized state management for workout templates across the application.
 */

import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { subscribeToPlanGenerated } from './job-progress.store';
import { createPersistStorage } from './persist-storage';
import { registerSessionScoped } from './session-scope';
import { workoutService } from '../services/workout.service';
import { clearStartWorkoutOptionsCache } from '../utils/workout/start-options/start-answer';

import type {
  ID,
  SearchRequest,
  PaginationMeta,
  WorkoutTemplate,
} from '../types';
import type { FetchOptions } from './fetch-options';
import type { WorkoutTemplateInput } from '../validation/template.schemas';

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface WorkoutStoreState {
  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isFetching: boolean;

  // Error states
  error: string | null;
  createError: string | null;
  updateError: string | null;
  deleteError: string | null;
  fetchError: string | null;

  // Data state
  workoutTemplates: WorkoutTemplate[];
  workoutsPagination: PaginationMeta | null;
  recentWorkouts: WorkoutTemplate[];
  lastSearchParams: SearchRequest; // Last search, re-run after a restore
}

export interface WorkoutStoreActions {
  // Core workout template CRUD
  /** Resolves `true` when the workout was created, `false` when it failed (see `createError`). */
  createWorkout: (workoutData: WorkoutTemplateInput) => Promise<boolean>;
  fetchWorkouts: (
    searchParams?: SearchRequest,
    options?: FetchOptions
  ) => Promise<void>;
  /** Resolves `true` when the workout was updated, `false` when it failed (see `updateError`). */
  updateWorkout: (
    workoutId: ID,
    data: WorkoutTemplateInput
  ) => Promise<boolean>;
  deleteWorkout: (workoutId: ID) => Promise<boolean>;
  restoreWorkout: (workoutId: ID) => Promise<boolean>;

  // Search
  setSearchParams: (params: SearchRequest) => void;
  clearSearch: () => Promise<void>;

  // State management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearAllErrors: () => void;

  // Store reset
  reset: () => void;
}

export type WorkoutStore = WorkoutStoreState & WorkoutStoreActions;

// =============================================================================
// STORE CONFIGURATION
// =============================================================================

const STORAGE_KEY = 'personal-trainer-workout';

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const workoutStore = createStore<WorkoutStore>()(
  persist(
    (set, get) => ({
      // =======================================================================
      // INITIAL STATE
      // =======================================================================

      // Loading states
      isLoading: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetching: false,

      // Error states
      error: null,
      createError: null,
      updateError: null,
      deleteError: null,
      fetchError: null,

      // Data state
      workoutTemplates: [],
      workoutsPagination: null,
      recentWorkouts: [],
      lastSearchParams: {},

      // =======================================================================
      // WORKOUT TEMPLATE CRUD ACTIONS
      // =======================================================================

      createWorkout: async (
        workoutData: WorkoutTemplateInput
      ): Promise<boolean> => {
        set({ isCreating: true, createError: null });

        try {
          const newWorkout = await workoutService.createWorkout(workoutData);

          const { workoutTemplates } = get();
          set({
            workoutTemplates: [
              { ...newWorkout, completed: 0, previous: null },
              ...workoutTemplates,
            ],
          });
          return true;
        } catch (error) {
          const errorMessage = failureMessage(
            error,
            'Failed to create workout'
          );
          set({ createError: errorMessage });
          return false;
        } finally {
          set({ isCreating: false });
        }
      },

      fetchWorkouts: async (
        searchParams: SearchRequest = {},
        { fresh = false }: FetchOptions = {}
      ): Promise<void> => {
        if (fresh) workoutService.clearCache();
        set({
          isFetching: true,
          fetchError: null,
          lastSearchParams: searchParams,
        });

        try {
          // The whole list, never a page: the Workouts page filters and
          // searches client-side, so a page would hide the rest.
          const paginatedResponse = await workoutService.getWorkouts({
            ...searchParams,
            paginate: false,
          });
          set({
            workoutTemplates: paginatedResponse.data,
            workoutsPagination: paginatedResponse.meta,
          });
        } catch (error) {
          const errorMessage = failureMessage(
            error,
            'Failed to fetch workouts'
          );
          set({ fetchError: errorMessage });
        } finally {
          set({ isFetching: false });
        }
      },

      updateWorkout: async (
        workoutId: ID,
        data: WorkoutTemplateInput
      ): Promise<boolean> => {
        set({ isUpdating: true, updateError: null });

        try {
          const updatedWorkout = await workoutService.updateWorkout(
            workoutId,
            data
          );
          const { workoutTemplates } = get();
          const updatedTemplates = workoutTemplates.map((workout) =>
            workout.id === workoutId
              ? {
                  ...updatedWorkout,
                  completed: workout.completed,
                  previous: workout.previous,
                }
              : workout
          );

          set({ workoutTemplates: updatedTemplates });
          return true;
        } catch (error) {
          const errorMessage = failureMessage(
            error,
            'Failed to update workout'
          );
          set({ updateError: errorMessage });
          return false;
        } finally {
          set({ isUpdating: false });
        }
      },

      deleteWorkout: async (workoutId: ID): Promise<boolean> => {
        set({ isDeleting: true, deleteError: null });

        try {
          await workoutService.deleteWorkout(workoutId);

          const { workoutTemplates } = get();
          const filteredTemplates = workoutTemplates.filter(
            (workout) => workout.id !== workoutId
          );

          set({ workoutTemplates: filteredTemplates });
          return true;
        } catch (error) {
          const errorMessage = failureMessage(
            error,
            'Failed to delete workout'
          );
          set({ deleteError: errorMessage });
          return false;
        } finally {
          set({ isDeleting: false });
        }
      },

      restoreWorkout: async (workoutId: ID): Promise<boolean> => {
        set({ isDeleting: true, deleteError: null });

        try {
          await workoutService.restoreWorkout(workoutId);

          // Re-sync from the server so the restored workout reappears with its
          // canonical position and computed fields (completed/previous).
          const { lastSearchParams, fetchWorkouts } = get();
          await fetchWorkouts(lastSearchParams);
          return true;
        } catch (error) {
          const errorMessage = failureMessage(
            error,
            'Failed to restore workout'
          );
          set({ deleteError: errorMessage });
          return false;
        } finally {
          set({ isDeleting: false });
        }
      },

      // =======================================================================
      // SEARCH ACTIONS
      // =======================================================================

      setSearchParams: (params: SearchRequest): void => {
        set({ lastSearchParams: params });
      },

      clearSearch: async (): Promise<void> => {
        const { fetchWorkouts } = get();
        set({ lastSearchParams: {} });
        await fetchWorkouts({});
      },

      // =======================================================================
      // STATE MANAGEMENT ACTIONS
      // =======================================================================

      setLoading: (loading: boolean): void => {
        set({ isLoading: loading });
      },

      setError: (error: string | null): void => {
        set({ error });
      },

      clearError: (): void => {
        set({ error: null });
      },

      clearAllErrors: (): void => {
        set({
          error: null,
          createError: null,
          updateError: null,
          deleteError: null,
          fetchError: null,
        });
      },

      reset: (): void => {
        set({
          // Loading states
          isLoading: false,
          isCreating: false,
          isUpdating: false,
          isDeleting: false,
          isFetching: false,

          // Error states
          error: null,
          createError: null,
          updateError: null,
          deleteError: null,
          fetchError: null,

          // Data state
          workoutTemplates: [],
          workoutsPagination: null,
          recentWorkouts: [],
          lastSearchParams: {},
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createPersistStorage(),
      partialize: (state) => ({
        // Persist recent workouts,
        recentWorkouts: state.recentWorkouts,
        // Don't persist workoutTemplates - fetch from server
      }),
      version: 1,
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

// Loading selectors
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectWorkoutLoading = (state: WorkoutStore) => ({
  isLoading: state.isLoading,
  isCreating: state.isCreating,
  isUpdating: state.isUpdating,
  isDeleting: state.isDeleting,
  isFetching: state.isFetching,
});

// Error selectors
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectWorkoutErrors = (state: WorkoutStore) => ({
  error: state.error,
  createError: state.createError,
  updateError: state.updateError,
  deleteError: state.deleteError,
  fetchError: state.fetchError,
});

// Data selectors
export const selectWorkoutTemplates = (
  state: WorkoutStore
): WorkoutTemplate[] => state.workoutTemplates;

export const selectWorkoutsPagination = (
  state: WorkoutStore
): PaginationMeta | null => state.workoutsPagination;

export const selectRecentWorkouts = (state: WorkoutStore): WorkoutTemplate[] =>
  state.recentWorkouts;

// Computed selectors
export const selectWorkoutById =
  (workoutId: ID) =>
  (state: WorkoutStore): WorkoutTemplate | undefined =>
    state.workoutTemplates.find((workout) => workout.id === workoutId);

export const selectWorkoutsByPlan =
  (planId: ID) =>
  (state: WorkoutStore): WorkoutTemplate[] =>
    state.workoutTemplates.filter((workout) => workout.plan?.id === planId);

// Count selector
export const selectTotalWorkouts = (state: WorkoutStore): number =>
  state.workoutsPagination?.total ?? 0;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const getWorkoutTemplates = (): WorkoutTemplate[] =>
  workoutStore.getState().workoutTemplates;

// =============================================================================
// SESSION SCOPE
// =============================================================================

// =============================================================================
// PLAN GENERATION
// =============================================================================

// A generated plan arrives with its workouts, made on the server, so nothing
// here invalidated the cached list: drop it, and read a list already loaded
// again so a mounted Workouts screen shows them. The start-workout flow's
// remembered answer goes too, for the same reason.
subscribeToPlanGenerated(() => {
  clearStartWorkoutOptionsCache();
  const { workoutsPagination, lastSearchParams, fetchWorkouts } =
    workoutStore.getState();
  if (workoutsPagination === null) {
    workoutService.clearCache();
    return;
  }
  void fetchWorkouts(lastSearchParams, { fresh: true });
});

registerSessionScoped('workoutStore', () => {
  workoutStore.getState().reset();
});
